import { useCanViewEmails } from '../../../../helpers/useCanViewEmails';
import React, { useEffect, useRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { Tooltip } from '@mui/material';
import { cloneDeep } from 'lodash';
import cs from 'classnames';

import RoleDropdown from '../../../UsersList/CreateSuperAdminModal/RoleDropDown';
import { getMyUserInfo, getUserById, updateUserByAdmin } from '../../../../api/usersApi';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { ContainedButton, OutlinedButton } from '../../Buttons';
import { saveUser } from '../../../../store/user/userSlice';
import { ORGANIZATION, ROLE_DESCRIPTION, USER } from '../../../../enums';
import { NOTIFICATION } from '../../../../enums/ENUMS';
import { AxiosError } from '../../../../api/axios';
import styles from './EditUserModal.module.scss';
import { ROLES, RolesLowerCase } from '../../../../enums/ROLES';
import { User } from '../../OrganizationCard';
import RoleSelector from '../../RoleSelector';
import RolePlate from '../../RolePlate';
import Avatar from '../../Avatar';
import Modal from '../../Modal';
import AppendOrgModal from './AppendOrgModal';
import { getAllOrganizations, patchOrganization } from '../../../../api';
import { useHotkeys } from 'react-hotkeys-hook';
import { enqueueSnackbar } from 'notistack';

type TAdminEditUserModalProps = {
  isOpen: boolean;
  switchModal: () => void;
  user: User | USER;
  fetchData: () => Promise<unknown>;
};

const EditUserAdminModal: React.FC<TAdminEditUserModalProps> = ({ isOpen, switchModal, user, fetchData }) => {
  const canViewEmails = useCanViewEmails();
  const { allRoles, currentUser, role: currentUserRole, myOrganization } = useAppSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const updateButtonRef = useRef<HTMLDivElement>(null);
  const notification = useSnackbar();
  const [passwordConfirmed, setPasswordConfirmed] = useState(true);
  const dispatch = useAppDispatch();
  const isSuperAdmin = currentUserRole === ROLES.SUPER_ADMIN;
  const isUnregistered = !!user.invitation_token;

  const [role, setRole] = useState(user?.role?.title);
  const handleChange = (rolestr: string) => setRole(rolestr);

  const [newEmail, setNewEmail] = useState(user.email);
  const changeEmail = (email: string) => setNewEmail(email);

  const [newUsername, setNewUsername] = useState(user.username);
  const changeUsername = (email: string) => setNewUsername(email);

  const [newPassword, setNewPassword] = useState('');
  const changePassword = (password: string) => setNewPassword(password);
  const [activateRemotely, setActivateRemotely] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [serverTempPassword, setServerTempPassword] = useState('');
  const [allowDownloadOwnComposers, setAllowDownloadOwnComposers] = useState(true);
  const [initialAllowDownloadOwnComposers, setInitialAllowDownloadOwnComposers] = useState(true);
  const [changedOrgIds, setChangedOrgIds] = useState<Record<string, boolean>>({});

  const handlePasswordConfirm = (password: string) => {
    if (password !== newPassword) {
      setPasswordConfirmed(false);
    } else {
      setPasswordConfirmed(true);
    }
  };

  const onClose = () => {
    setNewEmail(user.email);
    setNewUsername(user.username);
    setRole(user.role.title);
    setPasswordConfirmed(true);
    setNewPassword('');
    setActivateRemotely(false);
    setGeneratedPassword('');
    setServerTempPassword('');
    setAllowDownloadOwnComposers(initialAllowDownloadOwnComposers);
    switchModal();
    setUserRoles([]);
    setChangedUserRoles(false);
    setChangedOrgIds({});
    setNewOrgs([]);
    setExpandMore(false);
  };

  useEffect(() => {
    if (user) {
      setRole(user.role.title);
      setNewEmail(user.email);
      setNewUsername(user.username);
      setActivateRemotely(false);
      setGeneratedPassword('');
      setServerTempPassword('');
      setAllowDownloadOwnComposers(true);
      setInitialAllowDownloadOwnComposers(true);
    }
  }, [user]);

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*';
    const length = 12;
    let result = '';
    for (let i = 0; i < length; i += 1) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const toggleActivateRemotely = (checked: boolean) => {
    setActivateRemotely(checked);
    setPasswordConfirmed(true);
    if (checked) {
      setGeneratedPassword(generateRandomPassword());
    } else {
      setGeneratedPassword('');
    }
  };

  const submitEditRequest = async () => {
    const activationPassword = activateRemotely && isSuperAdmin && isUnregistered ? generatedPassword : '';
    const allowDownloadOwnComposersChanged = allowDownloadOwnComposers !== initialAllowDownloadOwnComposers;
    const hasOrgUpdates = Object.keys(changedOrgIds).length > 0;
    if (
      !newPassword &&
      !activationPassword &&
      role === user.role.title &&
      newUsername === user.username &&
      newEmail === user.email &&
      !changedUserRoles &&
      !allowDownloadOwnComposersChanged &&
      !hasOrgUpdates
    ) {
      onClose();
      return;
    }

    setLoading(true);
    try {
      const roleId = allRoles.find((item) => item.title === role)?.id as string;
      const reqBody: {
        email?: string;
        username?: string;
        role_id?: string;
        userId: string;
        password?: string;
        organization_id?: string;
        activate_remotely?: boolean;
        allow_download_own_composers?: boolean;
      } = {
        email: newEmail,
        username: newUsername,
        role_id: roleId,
        userId: user.id,
        password: activationPassword || newPassword,
      };
      if (newEmail === user.email) {
        delete reqBody.email;
      }
      if (newUsername === user.username || !newUsername.trim()) {
        delete reqBody.username;
      }
      if (!newPassword && !activationPassword) {
        delete reqBody.password;
      }
      if (activationPassword) {
        reqBody.activate_remotely = true;
      }

      if (currentUserRole !== ROLES.SUPER_ADMIN && myOrganization?.id) {
        reqBody.organization_id = myOrganization.id;
      }
      if (allowDownloadOwnComposersChanged && myOrganization?.id) {
        reqBody.allow_download_own_composers = allowDownloadOwnComposers;
      }
      if ((changedUserRoles || hasOrgUpdates) && userRoles.length) {
        delete reqBody.role_id;
        for (let i = 0; i < userRoles.length; i++) {
          try {
            const obj = userRoles[i];
            if (hasOrgUpdates && !changedOrgIds[obj.organization.id]) {
              continue;
            }
            await updateUserByAdmin({
              role_id: obj.role.id,
              allow_download_own_composers: obj.allow_download_own_composers,
              userId: user.id,
              organization_id: obj.organization.id,
            });
          } catch (err) {
            const error = err as AxiosError;
            console.log(error.response.data.message);
            if (error?.response?.data?.message) {
              enqueueSnackbar(error.response.data.message, {
                variant: 'error',
                persist: true,
                anchorOrigin: {
                  horizontal: 'left',
                  vertical: 'bottom',
                },
              });
            }
          }
        }
      }

      if (newOrgs.length) {
        for (let i = 0; i < newOrgs.length; i++) {
          try {
            const obj = newOrgs[i];
            await patchOrganization(obj.orgId, {
              newUsers: [
                { email: user.email.toLocaleLowerCase(), username: user?.username ? user.username : 'User', role_id: obj.role.id },
              ],
            });
          } catch (err) {
            const error = err as AxiosError;
            console.log(error.response.data.message);
            if (error?.response?.data?.message) {
              enqueueSnackbar(error.response.data.message, {
                variant: 'error',
                persist: true,
                anchorOrigin: {
                  horizontal: 'left',
                  vertical: 'bottom',
                },
              });
            }
          }
        }
      }

      await updateUserByAdmin(reqBody);
    } catch (err) {
      const error = err as AxiosError;
      console.log(error.response.data.message);
      if (error?.response?.data?.message) {
        enqueueSnackbar(error.response.data.message, {
          variant: 'error',
          persist: true,
          anchorOrigin: {
            horizontal: 'left',
            vertical: 'bottom',
          },
        });
      }
    } finally {
      onClose();
      setTimeout(async () => {
        await fetchData();
        const userJson = await getMyUserInfo();
        dispatch(saveUser(userJson.data));
      }, 1000);
      setLoading(false);
    }
  };

  const [changedUserRoles, setChangedUserRoles] = useState(false);
  const [userRoles, setUserRoles] = useState<
    { organization: ORGANIZATION; role: ROLE_DESCRIPTION; allow_download_own_composers?: boolean }[]
  >([]);
  const onChangeUserRole = (organization_id: string, roleObj: ROLE_DESCRIPTION) => {
    const arr = cloneDeep(userRoles);
    const existingIndex = arr.findIndex((role) => role.organization.id === organization_id);
    if (existingIndex !== -1) {
      arr[existingIndex].role = { ...roleObj };
    }
    setUserRoles(arr);
  };
  const onChangeOrgDownloadPermission = (organization_id: string, allowDownload: boolean) => {
    const arr = cloneDeep(userRoles);
    const existingIndex = arr.findIndex((role) => role.organization.id === organization_id);
    if (existingIndex !== -1) {
      arr[existingIndex].allow_download_own_composers = allowDownload;
    }
    setUserRoles(arr);
  };
  const monitor = (e: string, organization_id: string) => {
    const roleObj = allRoles.find((r) => r.title === e);
    if (roleObj && organization_id) {
      setChangedUserRoles(true);
      setChangedOrgIds((prev) => ({ ...prev, [organization_id]: true }));
      onChangeUserRole(organization_id, roleObj);
    }
  };

  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [allOrgs, setAllOrgs] = useState<ORGANIZATION[]>([]);
  const [filteredOrgs, setFilteredOrgs] = useState<ORGANIZATION[]>([]);
  const fetchUser = async (id: string) => {
    try {
      setLoading(true);
      const userRes = await getUserById(id);
      setServerTempPassword(userRes?.data?.temp_password ?? '');
      if (userRes?.data?.organization_users) {
        setOrganizationUsers(userRes.data.organization_users);

        setUserRoles(
          userRes?.data?.organization_users.map((obj: any) => ({
            organization: obj.organization,
            role: obj.role,
            allow_download_own_composers: obj.allow_download_own_composers,
          })),
        );
        const currentOrg = userRes?.data?.organization_users.find((obj: any) => obj.organization.id === myOrganization?.id);
        const allowFlag = typeof currentOrg?.allow_download_own_composers === 'boolean' ? currentOrg.allow_download_own_composers : true;
        setAllowDownloadOwnComposers(allowFlag);
        setInitialAllowDownloadOwnComposers(allowFlag);
      }

      if (currentUserRole === ROLES.SUPER_ADMIN) {
        const res = await getAllOrganizations({ page: 0, limit: 5000, search: '', selectedType: 'all' });
        if (res?.data.documents) {
          setAllOrgs(res.data.documents);
        }
        if (userRes?.data?.organization_users) {
          const existingOrganizationIds = userRes?.data?.organization_users.map((org: any) => org.organization.id);
          const remainingOrganizations = res.data.documents.filter((org: ORGANIZATION) => !existingOrganizationIds.includes(org.id));
          setFilteredOrgs(remainingOrganizations);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (isOpen) {
      fetchUser(user.id);
    }
  }, [user, isOpen]);

  const [expandMore, setExpandMore] = useState(false);
  const onExpandMore = () => {
    setExpandMore(true);
  };

  const [showAddOrgModal, setShowAddMoreOrgModal] = useState(false);
  const switchShowAddOrgModal = () => {
    setShowAddMoreOrgModal(!showAddOrgModal);
  };

  const [newOrgs, setNewOrgs] = useState<{ orgId: string; role: ROLE_DESCRIPTION; organization: ORGANIZATION }[]>([]);
  const onNewOrgSelect = (data: { orgId: string; role?: ROLE_DESCRIPTION; organization: ORGANIZATION }) => {
    const { orgId, role, organization } = data;
    setChangedUserRoles(true);
    const existIndex = newOrgs.findIndex((org) => org.orgId === orgId);
    const oa = allRoles.find((r) => r.title === RolesLowerCase.ORGANIZATION_ADMIN);
    let arr = cloneDeep(newOrgs);
    if (existIndex === -1 && oa) {
      // new org
      arr.push({ orgId, role: { ...oa }, organization });
    } else if (existIndex >= 0 && role) {
      // select role, after org was selected
      arr[existIndex] = { orgId, role, organization };
    } else if (existIndex >= 0 && !role) {
      // select org, that wasnt selected before
      arr = newOrgs.filter((o, i) => i !== existIndex);
    }
    setNewOrgs(arr);
  };
  const cancelNewOrgsSelection = () => {
    setNewOrgs([]);
    setShowAddMoreOrgModal(false);
  };

  useHotkeys('Enter', () => {
    if (isOpen && !loading) updateButtonRef?.current?.click();
  });

  const showTempPassword = isSuperAdmin && !!serverTempPassword;

  return (
    <Modal open={isOpen} onClose={loading ? console.log : onClose} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.modalTitle}>Edit {currentUserRole !== ROLES.SUPER_ADMIN ? 'Profile' : 'User'}</div>

        {!isSuperAdmin && (
          <div className={styles.userContainer}>
            <div className={styles.user}>
              <Avatar avatar={user?.avatar_url} name={user.email} />
              {canViewEmails && (<Tooltip title={user.email}>
                <div className={styles.userEmail}> {user.email} </div>
              </Tooltip>)}
              {user.invitation_token ? (
                <Tooltip title="Not activated">
                  <div className={styles.notActive}>
                    <RolePlate userRole={user.role.title} />
                  </div>
                </Tooltip>
              ) : (
                <RolePlate userRole={user.role.title} />
              )}
            </div>
          </div>
        )}

        <div className={styles.form} style={{ marginTop: !isSuperAdmin ? '100px' : '0' }}>
          {user.username && (
            <div className={styles.field}>
              <input onChange={(e) => changeUsername(e.target.value)} type="username" className={styles.input} value={newUsername} />
            </div>
          )}

          {canViewEmails && (<div className={cs(styles.field, styles.emailField, !user.invitation_token ? styles.disabled : '')}>
            <span className={styles.emailLabel}>Email</span>
            <input
              onChange={(e) => changeEmail(e.target.value)}
              type="email"
              disabled={!user.invitation_token}
              className={styles.emailInput}
              value={newEmail}
            />
          </div>)}

          {currentUser?.id !== user.id && !isSuperAdmin && (
            <div className={styles.roleSelector}>
              <RoleSelector width="338px" defaultValue={role} onSelect={(role) => handleChange(role)} index={0} />
            </div>
          )}
          {!isSuperAdmin && [RolesLowerCase.ANALYST, RolesLowerCase.VIEWER].includes(role as RolesLowerCase) && (
            <label className={styles.downloadToggle}>
              <input type="checkbox" checked={allowDownloadOwnComposers} onChange={(e) => setAllowDownloadOwnComposers(e.target.checked)} />
              <span>Allow to Download their own online composers</span>
            </label>
          )}

          {showTempPassword && (
            <div className={styles.field}>
              <span className={styles.passwordLabel}>Temporary password</span>
              <input readOnly type="text" value={serverTempPassword} className={styles.input} />
            </div>
          )}

          {isSuperAdmin && isUnregistered ? (
            <div className={styles.activationSection}>
              <label className={styles.activationLabel}>
                <input
                  className={styles.activationCheckbox}
                  type="checkbox"
                  checked={activateRemotely}
                  onChange={(e) => toggleActivateRemotely(e.target.checked)}
                />
                Activate Remotely
              </label>
              {activateRemotely && (
                <div className={styles.field}>
                  <span className={styles.passwordLabel}>Generated password</span>
                  <input readOnly type="text" value={generatedPassword} className={styles.input} />
                  <div className={styles.helperText}>Share this password if the user does not receive the email.</div>
                </div>
              )}
            </div>
          ) : !showTempPassword ? (
            <>
              <div className={styles.field}>
                <input
                  disabled={!!user.invitation_token}
                  onChange={(e) => changePassword(e.target.value)}
                  type={'text'}
                  placeholder="Password"
                  className={styles.input}
                />
              </div>

              <div className={styles.field}>
                <input
                  disabled={!!user.invitation_token}
                  onChange={(e) => handlePasswordConfirm(e.target.value)}
                  type={'text'}
                  placeholder="Confirm password"
                  className={styles.input}
                  required={!!newPassword}
                />
              </div>
            </>
          ) : null}

          {isSuperAdmin && (
            <div className={styles.orgList} id="org_list">
              {userRoles
                ?.filter((r, i) => (expandMore ? true : i <= 3))
                .map((obj) => (
                  <div key={obj?.organization?.id} style={{ marginBottom: '12px' }}>
                    <div className={styles.org}>
                      <div className={styles.orgTitle}>{obj?.organization?.name}</div>
                      <div className={styles.orgRight}>
                        <RoleDropdown onSelect={(e) => monitor(e, obj?.organization?.id)} selectedRole={obj.role.title} />
                      </div>
                    </div>
                    {[RolesLowerCase.ANALYST, RolesLowerCase.VIEWER].includes(obj.role.title as RolesLowerCase) && (
                      <label className={styles.orgDownloadToggle}>
                        <input
                          type="checkbox"
                          checked={obj.allow_download_own_composers ?? true}
                          onChange={(e) => {
                            setChangedUserRoles(true);
                            setChangedOrgIds((prev) => ({ ...prev, [obj.organization.id]: true }));
                            onChangeOrgDownloadPermission(obj.organization.id, e.target.checked);
                          }}
                        />
                        <span>Allow to Download their own online composers</span>
                      </label>
                    )}
                  </div>
                ))}
              {userRoles.length > 4 && !expandMore && (
                <div className={styles.shadowContaier}>
                  <div className={styles.expandMore} onClick={onExpandMore}>
                    {' '}
                    Show all <KeyboardArrowDownIcon />{' '}
                  </div>
                </div>
              )}

              {newOrgs.length > 0 && (
                <div>
                  <div style={{ marginBottom: '8px' }}>New organisations: </div>
                  {newOrgs.map((obj) => (
                    <div key={obj?.organization.id} className={styles.org}>
                      <div className={styles.orgTitle}>{obj?.organization.name}</div>
                      <div className={styles.orgRight}>
                        <RolePlate userRole={obj.role.title} />
                        <ContainedButton
                          style={{ marginLeft: '4px' }}
                          kind="red"
                          onClick={() => onNewOrgSelect({ orgId: obj.orgId, organization: obj.organization })}
                        >
                          {' '}
                          Remove{' '}
                        </ContainedButton>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className={styles.addMore} onClick={switchShowAddOrgModal}>
                + Add to Organisation
              </div>
              <AppendOrgModal
                selectedOrgs={newOrgs}
                orgList={filteredOrgs}
                open={showAddOrgModal}
                switchModal={switchShowAddOrgModal}
                onSelect={onNewOrgSelect}
                cancelSelection={cancelNewOrgsSelection}
              />
            </div>
          )}

          <div className={styles.actionBtns}>
            <ContainedButton onClick={submitEditRequest} disabled={loading || !passwordConfirmed}>
              <div ref={updateButtonRef}>Update</div>
            </ContainedButton>

            <OutlinedButton onClick={onClose} disabled={loading} kind="default">
              Cancel
            </OutlinedButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditUserAdminModal;
